<template>
  <SearchFilterBar :store="store" :summary="filterSummary" placeholder="Search by shipment ID...">
    <div class="is-flex is-flex-wrap-wrap gap-2">
      <div class="field">
        <label class="label">Status</label>
        <MultiSelectCombobox
          :model-value="displayedStates"
          :items="stateOptions"
          placeholder="Filter by status..."
          @update:model-value="store.setFilter('states', $event)"
        >
          <template #selected-item="{ value, remove }">
            <ShipmentState :state="value" removable @remove="remove()" />
          </template>
        </MultiSelectCombobox>
      </div>

      <div v-if="receivers.length > 0" class="field">
        <label class="label">Receiver</label>
        <MultiSelectCombobox
          :model-value="store.receiverIds"
          :items="receivers"
          placeholder="Filter by receiver..."
          @update:model-value="store.setFilter('receiverIds', $event)"
        />
      </div>

      <div v-if="transporters.length > 0" class="field">
        <label class="label">Transporter</label>
        <MultiSelectCombobox
          :model-value="store.transporterIds"
          :items="transporters"
          placeholder="Filter by transporter..."
          @update:model-value="store.setFilter('transporterIds', $event)"
        />
      </div>
    </div>

    <div class="field">
      <label class="label">Est. Ship</label>
      <div class="columns is-mobile is-flex-wrap-wrap">
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="From"
            :value="store.estimatedShipBeginDate"
            @change="store.setFilter('estimatedShipBeginDate', $event.target.value || null)"
          />
        </div>
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="To"
            :value="store.estimatedShipEndDate"
            @change="store.setFilter('estimatedShipEndDate', $event.target.value || null)"
          />
        </div>
      </div>
    </div>

    <div class="field">
      <label class="label">Shipped</label>
      <div class="columns is-mobile is-flex-wrap-wrap">
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="From"
            :value="store.shippedBeginDate"
            @change="store.setFilter('shippedBeginDate', $event.target.value || null)"
          />
        </div>
        <div class="column">
          <input
            class="input"
            type="date"
            placeholder="To"
            :value="store.shippedEndDate"
            @change="store.setFilter('shippedEndDate', $event.target.value || null)"
          />
        </div>
      </div>
    </div>
  </SearchFilterBar>
</template>

<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import { startCase } from "lodash-es";
  import { parseISO, format } from "date-fns";
  import MultiSelectCombobox from "./shared/multi-select-combobox.vue";
  import SearchFilterBar from "./shared/search-filter-bar.vue";
  import ShipmentState from "./shipment-state.vue";
  import { useShipmentSearchStore } from "./stores/shipment-search-store";

  const stateOptions = [
    { value: "draft", label: "Draft" },
    { value: "active", label: "Active" },
    { value: "shipped", label: "Shipped" },
    { value: "cancelled", label: "Cancelled" },
  ];

  /* What the server falls back to when `states` is blank, mirrored here so the picker
   * and the summary show the states actually being listed. */
  const DEFAULT_STATES = ["draft", "active", "shipped"];

  const props = defineProps({
    receivers: { type: Array, default: () => [] },
    transporters: { type: Array, default: () => [] },
  });

  const store = useShipmentSearchStore();
  const { states } = storeToRefs(store);

  const displayedStates = computed(() => (states.value.length > 0 ? states.value : DEFAULT_STATES));

  function namesFor(ids, options) {
    return ids.map((id) => options.find((o) => o.value === id)?.label).filter(Boolean);
  }

  const filterSummary = computed(() => {
    const parts = [`States: ${displayedStates.value.map(startCase).join(", ")}`];

    const receivers = namesFor(store.receiverIds, props.receivers);
    if (receivers.length > 0) parts.push(`Receivers: ${receivers.join(", ")}`);

    const transporters = namesFor(store.transporterIds, props.transporters);
    if (transporters.length > 0) parts.push(`Transporters: ${transporters.join(", ")}`);

    if (store.estimatedShipBeginDate || store.estimatedShipEndDate) {
      const from = store.estimatedShipBeginDate
        ? format(parseISO(store.estimatedShipBeginDate), "MMM d, yyyy")
        : "…";
      const to = store.estimatedShipEndDate
        ? format(parseISO(store.estimatedShipEndDate), "MMM d, yyyy")
        : "…";
      parts.push(`Est. Ship: ${from} – ${to}`);
    }
    if (store.shippedBeginDate || store.shippedEndDate) {
      const from = store.shippedBeginDate
        ? format(parseISO(store.shippedBeginDate), "MMM d, yyyy")
        : "…";
      const to = store.shippedEndDate ? format(parseISO(store.shippedEndDate), "MMM d, yyyy") : "…";
      parts.push(`Shipped: ${from} – ${to}`);
    }
    if (store.q) parts.unshift(`Searching: "${store.q}"`);

    return parts.join(" · ");
  });
</script>
